import React, { useEffect } from 'react'
import createStore from 'teaful'
import { Form, Button, Input } from '@guillotinaweb/react-gmi'
import { InputNumber, message, Select } from 'antd'

import { BOTTLE_CAPACITY_LITERS, setLangs } from '../../consts'
import { CellarSelector } from '../../components/cellar-selector'
import { ContentLayout } from '../../common/content-layout'
import { GrapesSelector } from '../../components/grapes-selector'
import { I18NField } from '../../components/i18nfield'
import { Label } from '../../components/label'
import { ProductName } from '../../components/product-name'
import { RegionSelector } from '../../components/region-selector'
import { SelectOrphanOptions } from '../../components/select-orphan-options'
import { SelectTaxonomy } from '../../components/select-taxonomy'
import { getShopCountryHeaders } from '../../utils'

const NAME_BANNER_LENGTH = 40
const NAME_SHORT_LENGTH = 30

const initProduct = {
  '@type': 'Producto',
  nombres: setLangs({
    nombre: '',
    nombre_corto: '',
    nombre_banner: '',
  }),
}

const initOptionBO = {
  capacidad: '75',
}

export const { useStore: useState, getStore: getState } = createStore()
export const { useStore: useProduct, getStore: getProduct } = createStore(
  initProduct
)
export const { useStore: useOptionBO, getStore: getOptionBO } = createStore(
  initOptionBO
)
const [, setState] = getState()
const [, setProduct] = getProduct()
const [, setOptionBO] = getOptionBO()

function resetStores() {
  setProduct(initProduct)
  setOptionBO(initOptionBO)
  setState({ loading: false })
}

function create() {
  const [state] = getState()
  const [producto] = getProduct()
  const [optionBO] = getOptionBO()
  const token = localStorage.getItem('token')
  const options = { ...optionBO }

  if (state.loading) return
  if (!producto.id) return message.warning('Selecciona un producto')
  if (!producto.tipo) return message.warning('Selecciona el tipo')
  if (!state.cellar) return message.warning('Selecciona una bodega')
  if (!options.anyada) options.anyada = null

  setState((s) => ({ ...s, loading: true }))
  fetch(
    `/db/guillotina/bodegas/${state.cellar}/@create-vino-destilado-from-opcion`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: token ? `Bearer ${token}` : '',
        ...getShopCountryHeaders(),
      },
      body: JSON.stringify({ producto, ...options }),
    }
  )
    .then(async (r) => {
      if (r.status >= 300) {
        const error = await r.json()
        throw new Error(error.message)
      }
      window.location.href = `/-admin?path=/bodegas/${state.cellar}/${producto.id}/`
    })
    .catch((e) => {
      message.error(
        `Error: No se ha podido dar de alta el producto. ${e.message}`
      )
      setState((s) => ({ ...s, loading: false }))
    })
}

export function CreateProductContent() {
  const [loading] = useState.loading(false)
  const [name] = useProduct.nombre()

  useEffect(() => () => resetStores(), [])

  return (
    <ContentLayout loading={loading} title="Create product content">
      <Form onSubmit={create}>
        <OrhpanOptions />
        <ProductNewName />
        <Reference />
        <Vintage />
        <Capacity />
        <Graduation />
        <Cellar />
        <Type />
        <Region />
        <Grape />
        <div style={{ marginTop: 20 }} className="level level-right">
          <Button disabled={loading} className="is-primary" loading={loading}>
            Register {name}
          </Button>
        </div>
      </Form>
    </ContentLayout>
  )
}

function OrhpanOptions() {
  const [productId, setProductId] = useProduct.id()

  return (
    <>
      <Label>Selecciona producto para dar de alta</Label>
      <SelectOrphanOptions
        value={productId}
        onChange={({
          id,
          name,
          id_tipo_producto: type,
          tienda,
          id_producto: referencia,
        }) => {
          const [, setNames] = getProduct.nombres()
          const [, setParentType] = getState.parentType()
          const nombre = removeVintageAndFormat(name)
          setProductId(id)
          setParentType(type)
          setOptionBO((options) => ({ ...options, tienda, referencia }))
          setNames(
            setLangs({
              nombre,
              nombre_corto: nombre.slice(0, NAME_SHORT_LENGTH),
              nombre_banner: nombre.slice(0, NAME_BANNER_LENGTH),
            })
          )
        }}
      />
    </>
  )
}

function ProductNewName() {
  const [productId] = useProduct.id()
  const [, setName] = useProduct.nombre()
  const [names, setNames] = useProduct.nombres()

  useEffect(() => {
    setName(names.es.nombre)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names.es.nombre])

  return (
    <>
      <Label>
        Nuevo nombre
        <small
          style={{
            fontStyle: 'italic',
            fontWeight: 'normal',
            fontSize: 12,
            marginLeft: 10,
          }}
        >
          Tiene que ir sín la añada y sín el formato
        </small>
      </Label>
      <I18NField key={productId} Widget={() => null}>
        {(lang) => (
          <React.Fragment key={`name_${lang}`}>
            <ProductName
              required
              label="full name"
              names={names}
              lang={lang}
              field="nombre"
              setNames={setNames}
            />
            <ProductName
              required
              label="short name"
              names={names}
              lang={lang}
              maxLength={NAME_SHORT_LENGTH}
              field="nombre_corto"
              setNames={setNames}
            />
            <ProductName
              required
              label="banner name"
              names={names}
              maxLength={NAME_BANNER_LENGTH}
              lang={lang}
              field="nombre_banner"
              setNames={setNames}
            />
          </React.Fragment>
        )}
      </I18NField>
    </>
  )
}

function Cellar() {
  const [cellar, setCellar] = useState.cellar()

  return (
    <>
      <Label>Bodega</Label>
      <CellarSelector value={cellar} onChange={setCellar} />
    </>
  )
}

function Type() {
  const [parentType] = useState.parentType()
  const [type, setType] = useProduct.tipo()

  return (
    <>
      <Label>Type</Label>
      <SelectTaxonomy
        taxonomy="tipo_productos"
        value={type}
        key={parentType}
        filterByParentIds={parentType ? [parentType] : undefined}
        multiple={false}
        startsWithSlash
        onChange={setType}
        placeholder="Select type"
      />
    </>
  )
}

function Region() {
  const [region, setRegion] = useProduct.region.tree()

  return (
    <>
      <Label>Region</Label>
      <RegionSelector
        map={(m) => ({
          id: m.path.replace('/zonas', ''),
          name: m.title || m.id,
        })}
        value={region}
        onChange={setRegion}
      />
    </>
  )
}

function Reference() {
  const [ref, setRef] = useOptionBO.referencia()
  return (
    <>
      <Label>Referencia</Label>
      <Input readOnly required value={ref} onChange={setRef} />
    </>
  )
}

function Vintage() {
  const [vintage, setVintage] = useOptionBO.anyada()
  return (
    <>
      <Label>Anyada</Label>
      <InputNumber
        style={{ width: '100%' }}
        min={0}
        max={3999}
        value={vintage}
        onChange={setVintage}
      />
    </>
  )
}

function Capacity() {
  const [capacity, setCapacity] = useOptionBO.capacidad()
  return (
    <>
      <Label>Capacidad</Label>
      <Select
        style={{ width: '100%' }}
        required
        value={capacity}
        onChange={setCapacity}
      >
        {Object.keys(BOTTLE_CAPACITY_LITERS).map((capacity) => (
          <Select.Option key={capacity} value={capacity}>
            {capacity}
          </Select.Option>
        ))}
      </Select>
    </>
  )
}

function Graduation() {
  const [grad, setGrad] = useOptionBO.graduacion()
  return (
    <>
      <Label>Graduacion</Label>
      <InputNumber
        style={{ width: '100%' }}
        min={0}
        step={0.1}
        value={grad}
        onChange={setGrad}
      />
    </>
  )
}

function Grape() {
  const [grapes, setGrapes] = useProduct.tags_by_category.uva()
  return (
    <>
      <Label>Uvas</Label>
      <GrapesSelector
        value={grapes}
        onChange={(ids) =>
          setGrapes(
            ids.map((id) => {
              const grape = (grapes || []).find((g) => g.id === id)
              if (!grape) return { id, otros_datos: { porcentaje: 0 } }
              return grape
            })
          )
        }
        onEditPercentage={setGrapes}
      />
    </>
  )
}

function removeVintageAndFormat(name) {
  return name
    .replace(/\[(.*)\]/, '')
    .replace(/\((.*)\)/, '')
    .replace(/(1|2)\d\d\d/, '')
    .trim()
}
